import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  SelectedLoad,
  LoadMetadata,
  ConsignmentRouteInfo,
  WaypointLocationInfo,
  getTransportModeIdFromName,
} from '../plannerTypes'
import { UnassignedLoad, UnassignedConsignment } from '../../types'

/**
 * Generates a unique signature for a set of waypoint locations.
 * This is used to identify consignments that share the same route (same locations in same order).
 * Consignments with identical signatures can be consolidated into a single leg.
 */
export const getWaypointSignature = (waypointLocations: WaypointLocationInfo[]): string => {
  if (waypointLocations.length === 0) return ''
  return [...waypointLocations]
    .sort((a, b) => a.sequence - b.sequence)
    .map((wp) => `${wp.locationId ?? 'unknown'}:${wp.sequence}`)
    .join('|')
}

type JourneyPlannerSelectionState = {
  selectedLoads: SelectedLoad[]
  loadMetadataById: Record<number, LoadMetadata>
  consignmentRoutes: ConsignmentRouteInfo[]
}

const initialState: JourneyPlannerSelectionState = {
  selectedLoads: [],
  loadMetadataById: {},
  consignmentRoutes: [],
}

const journeyPlannerSlice = createSlice({
  name: 'journeyPlannerSelection',
  initialState,
  reducers: {
    setSelectedLoads: (
      state,
      action: PayloadAction<{
        loads: SelectedLoad[]
        unassignedLoads: UnassignedLoad[]
        consignments?: UnassignedConsignment[]
      }>
    ) => {
      state.selectedLoads = action.payload.loads
      // Merge new metadata with existing metadata instead of replacing
      // This preserves metadata cached from the side panel
            const newMetadata = action.payload.unassignedLoads.reduce(
              (acc, load) => {
                acc[load.loadId] = {
                  loadId: load.loadId,
                  shipmentBookingId: load.shipmentBookingId,
                  trackingNumber: load.trackingNumber,
                  services: load.services,
                  loadTypeId: load.loadTypeId,
                  loadTypeName: load.loadTypeName,
                  quantity: load.quantity,
                }
                return acc
              },
              {} as Record<number, LoadMetadata>
            )
      state.loadMetadataById = { ...state.loadMetadataById, ...newMetadata }

      // Extract route info for each consignment and consolidate those with identical waypoints
      // This prevents creating redundant legs when multiple consignments share the same route
      state.consignmentRoutes = []
      if (action.payload.consignments && action.payload.consignments.length > 0) {
        // Map to group consignments by their waypoint signature
        const routesBySignature = new Map<string, ConsignmentRouteInfo>()

        for (const consignment of action.payload.consignments) {
          // Get load IDs for this consignment from selected loads
          const consignmentLoadIds = action.payload.loads
            .filter((sl) => sl.shipmentBookingId === consignment.shipmentBookingId)
            .map((sl) => sl.loadId)

          if (consignmentLoadIds.length === 0) continue

          let waypointLocations: WaypointLocationInfo[] = []
          let earliestScheduledTime: string | undefined
          let transportModeId: number | undefined

          if (consignment.waypoints && consignment.waypoints.length > 0) {
            // Extract waypoint locations sorted by sequence
            const sortedWaypoints = [...consignment.waypoints].sort(
              (a, b) => a.sequence - b.sequence
            )
            waypointLocations = sortedWaypoints.map((wp) => ({
              waypointId: wp.waypointId,
              locationId: wp.locationId,
              locationName: wp.locationName,
              address: (wp as any).address,
              sequence: wp.sequence,
            }))

            // Find earliest scheduled time from transit details
            for (const waypoint of sortedWaypoints) {
              if (waypoint.transitDetails && waypoint.transitDetails.length > 0) {
                for (const transit of waypoint.transitDetails) {
                  if (transit.scheduledDeparture) {
                    if (!earliestScheduledTime || transit.scheduledDeparture < earliestScheduledTime) {
                      earliestScheduledTime = transit.scheduledDeparture
                    }
                  }
                  // Get transport mode from first transit detail found
                  if (!transportModeId && transit.transportModeName) {
                    transportModeId = getTransportModeIdFromName(transit.transportModeName)
                  }
                }
              }
            }
          }

          // Generate signature for this consignment's waypoints
          const signature = getWaypointSignature(waypointLocations)

          // Check if we already have a route with this signature
          const existingRoute = routesBySignature.get(signature)
          if (existingRoute) {
            // Consolidate: merge load IDs into existing route
            existingRoute.loadIds.push(...consignmentLoadIds)
            // Update earliest scheduled time if this consignment has an earlier one
            if (earliestScheduledTime) {
              if (!existingRoute.earliestScheduledTime || earliestScheduledTime < existingRoute.earliestScheduledTime) {
                existingRoute.earliestScheduledTime = earliestScheduledTime
              }
            }
            // Use transport mode if existing route doesn't have one
            if (transportModeId && !existingRoute.transportModeId) {
              existingRoute.transportModeId = transportModeId
            }
          } else {
            // Create new route entry for this signature
            routesBySignature.set(signature, {
              shipmentBookingId: consignment.shipmentBookingId,
              waypointLocations,
              earliestScheduledTime,
              transportModeId,
              loadIds: consignmentLoadIds,
            })
          }
        }

        // Convert map values to array
        state.consignmentRoutes = Array.from(routesBySignature.values())

        // Sort consignment routes by earliest scheduled time
        state.consignmentRoutes.sort((a, b) => {
          if (!a.earliestScheduledTime && !b.earliestScheduledTime) return 0
          if (!a.earliestScheduledTime) return 1
          if (!b.earliestScheduledTime) return -1
          return a.earliestScheduledTime.localeCompare(b.earliestScheduledTime)
        })
      }
    },
        addLoadMetadata: (state, action: PayloadAction<UnassignedLoad[]>) => {
          action.payload.forEach((load) => {
            state.loadMetadataById[load.loadId] = {
              loadId: load.loadId,
              shipmentBookingId: load.shipmentBookingId,
              trackingNumber: load.trackingNumber,
              services: load.services,
              loadTypeId: load.loadTypeId,
              loadTypeName: load.loadTypeName,
              quantity: load.quantity,
            }
          })
        },
    clearSelection: (state) => {
      state.selectedLoads = []
      state.loadMetadataById = {}
      state.consignmentRoutes = []
    },
  },
})

export const { setSelectedLoads, addLoadMetadata, clearSelection } = journeyPlannerSlice.actions
export default journeyPlannerSlice.reducer
